import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormSubmissionDetails,
  FORM_VENDORS,
} from "../../../../common/types/form";
import { DataTable } from "../../../../components/data-table/DataTable";
import { FormatDate } from "../../../../components/DateTimeRangeFilter";
import IButton from "../../../../components/IButton";
import { useAppDispatch } from "../../../../store";
import {
  getFormSubmissions,
  selectForm,
  setFormSubmissionsPage,
} from "../formSlice";
import urls from "../../../../urls";
import { FaExternalLinkAlt } from "react-icons/fa";

export function LabelValuePair({
  label,
  value,
  isLoadingData,
}: {
  label: string;
  value: string | ReactNode;
  isLoadingData?: boolean;
}) {
  return (
    <VStack alignItems="flex-start" fontSize="14px" spacing={1}>
      <Text fontWeight={600} color="text.50">
        {label}
      </Text>
      {isLoadingData ? (
        <Skeleton h="20px" w="100px" isLoaded={!isLoadingData} />
      ) : (
        <Text wordBreak="break-all">{value ?? "-"}</Text>
      )}
    </VStack>
  );
}

function convertToLabelValuePair(jsonString: string) {
  const data = JSON.parse(jsonString ?? "{}");
  return Object.entries(data).map(([key, value]) => {
    return {
      label: key,
      value: (value as any).toString(),
    };
  });
}

function SubmissionSidebar({
  selectedSubmission,
  onClose,
}: {
  selectedSubmission: FormSubmissionDetails | null;
  onClose: () => void;
}) {
  const submissionData = useMemo(
    () => convertToLabelValuePair(selectedSubmission?.submission_data ?? "{}"),
    [selectedSubmission]
  );
  const mappedData = useMemo(
    () => convertToLabelValuePair(selectedSubmission?.mapped_data ?? "{}"),
    [selectedSubmission]
  );

  const mappedEmailAndId = useMemo(() => {
    if (mappedData.length) {
      const email = mappedData.find((data) => data.label === "email")?.value;
      const personId = mappedData.find(
        (data) => data.label === "person_id"
      )?.value;
      return {
        email,
        personId,
      };
    } else {
      return null;
    }
  }, [mappedData]);

  return (
    <Drawer
      isOpen={!!selectedSubmission}
      placement="right"
      onClose={onClose}
      size="sm"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="16px">
          {mappedEmailAndId &&
          (mappedEmailAndId.email || mappedEmailAndId.personId) ? (
            <Box>
              <Text>{mappedEmailAndId.email ?? mappedEmailAndId.personId}</Text>
              {mappedEmailAndId.personId && (
                <IButton
                  maxWidth="100%"
                  wordBreak="break-all"
                  onClick={() =>
                    window.open(
                      `${urls.person}/${mappedEmailAndId.personId}`,
                      "_blank"
                    )
                  }
                  rightIcon={<FaExternalLinkAlt />}
                  variant="link"
                  fontSize={12}
                  colorScheme="blue"
                >
                  Click to view details
                </IButton>
              )}
            </Box>
          ) : (
            "Submission details"
          )}
        </DrawerHeader>
        <DrawerBody>
          <VStack alignItems="flex-start" px={0} spacing={4}>
            {!!mappedData.length && (
              <VStack alignItems="flex-start" px={0} spacing={2}>
                <Text fontSize={15} fontWeight={600}>
                  Mapped data
                </Text>
                <VStack alignItems="flex-start" spacing="16px">
                  {mappedData.map(({ label, value }, index) => {
                    return (
                      <LabelValuePair
                        label={label}
                        value={value}
                        key={"mapped_data_" + index}
                      />
                    );
                  })}
                </VStack>
              </VStack>
            )}
            {!!submissionData.length && (
              <VStack alignItems="flex-start" px={0} spacing={2}>
                <Text fontSize={15} fontWeight={600}>
                  Submission data
                </Text>
                <VStack alignItems="flex-start" spacing="16px">
                  {submissionData.map(({ label, value }, index) => {
                    return (
                      <LabelValuePair
                        label={label}
                        value={value}
                        key={"submission_data_" + index}
                      />
                    );
                  })}
                </VStack>
              </VStack>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default function FormSubmissionTable({
  id,
  vendor,
}: {
  id: string;
  vendor: FORM_VENDORS;
}) {
  const dispatch = useAppDispatch();

  const { formSubmissionList } = useSelector(selectForm);

  useEffect(() => {
    return function resetPage() {
      dispatch(setFormSubmissionsPage(1));
    };
  }, [dispatch]);

  useEffect(() => {
    if (id)
      dispatch(
        getFormSubmissions({
          formId: id,
          formType: vendor,
        })
      );
  }, [id, vendor, dispatch, formSubmissionList.currentPageNo]);

  const columnHelper = createColumnHelper<FormSubmissionDetails>();
  const [selectedSubmission, setSelectedSubmission] =
    useState<FormSubmissionDetails | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor("submission_id", {
        header: "Submission id",
        minSize: 400,
      }),
      columnHelper.accessor("mapped_data", {
        cell: (info) => {
          if (info.getValue()) {
            return JSON.parse(info.getValue())?.email ?? "";
          } else {
            return "";
          }
        },
        header: "Email ID",
        minSize: 700,
      }),
      columnHelper.accessor("submission_date", {
        header: "Submission date",
        cell: (info) => <FormatDate date={info.getValue()} splitLines />,
        minSize: 200,
      }),
    ],
    [columnHelper]
  );

  return (
    <>
      <DataTable
        list={formSubmissionList.list}
        loading={formSubmissionList.fetchingList}
        setPage={(pageNo: number) => dispatch(setFormSubmissionsPage(pageNo))}
        onRowClick={(data: FormSubmissionDetails) =>
          setSelectedSubmission(data)
        }
        totalPageCount={formSubmissionList.totalPageCount}
        currentPage={formSubmissionList.currentPageNo}
        columns={columns}
        emptyMsg="No submissions"
      />
      <SubmissionSidebar
        selectedSubmission={selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
      />
    </>
  );
}
